import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "What happens when two antidepressants don't work?",
    answer:
      "If two antidepressants taken at adequate doses for 6 to 8 weeks have not provided adequate improvement, this may meet the definition of treatment-resistant depression. A psychiatrist may try a third medication, add an augmenting medication, add psychotherapy, or recommend a non-medication treatment like TMS, SAINT®, or SPRAVATO®.",
  },
  {
    question: "Does trying a third antidepressant help?",
    answer:
      "It can, but the STAR*D study showed diminishing returns: remission rates fell from about 37% after the first medication to 14% after the third. After two or three adequate trials, a psychiatrist may recommend a different approach rather than continuing to try more medications.",
  },
  {
    question: "Is TMS an option after two antidepressants fail?",
    answer:
      "Yes. TMS is FDA-cleared for adults with major depressive disorder who have not improved adequately on antidepressant medication. Most insurance plans require 1 to 4 adequate trials before authorizing TMS. A benefits check clarifies your plan's criteria.",
  },
  {
    question: "Can SAINT® help if multiple antidepressants haven't worked?",
    answer:
      "SAINT® is FDA-cleared for adults with major depressive disorder who have not achieved satisfactory improvement from prior antidepressant medication. A psychiatric evaluation determines eligibility. Individual results vary and no outcome is guaranteed.",
  },
];

export const pageTwoAntidepressantsDontWork: LearnPageData = {
  slug: "what-happens-when-two-antidepressants-dont-work",
  title: "When Two Antidepressants Don't Work | Neuro Wellness",
  description:
    "If two adequate antidepressant trials haven't worked, you may have treatment-resistant depression. Learn about TMS, SAINT®, SPRAVATO®, and what comes next.",
  canonical: "/learn/what-happens-when-two-antidepressants-dont-work",
  h1: "What Happens When Two Antidepressants Don't Work?",
  breadcrumbLabel: "When Two Antidepressants Don't Work",
  crumbs: baseCrumbs("When Two Antidepressants Don't Work"),
  shortAnswer:
    "If two antidepressants taken at adequate doses for 6 to 8 weeks have not provided adequate improvement, this may meet the definition of treatment-resistant depression. At this point, a psychiatrist may try a different medication class, add an augmenting medication, add psychotherapy, or recommend a non-medication treatment like TMS, SAINT®, or SPRAVATO®. The STAR*D study showed diminishing returns with each additional medication trial.",
  keyTakeaways: [
    "Two failed adequate trials may meet the definition of treatment-resistant depression.",
    "STAR*D showed diminishing returns: 37% remission after the first medication, 14% after the third.",
    "TMS, SAINT®, and SPRAVATO® are specifically indicated after antidepressant non-response.",
    "A psychiatrist determines the best next step based on your history.",
  ],

  sections: [
    {
      heading: "What 'Two Failed Trials' Means",
      body: (
        <>
          {P(
            "Two failed trials means you have taken two different antidepressants at therapeutic doses for at least 6 to 8 weeks each, without adequate improvement. This is the most common definition of treatment-resistant depression used in clinical research and by the FDA. It describes your treatment history, not a separate diagnosis.",
          )}
        </>
      ),
    },
    {
      heading: "Diminishing Returns With More Medications",
      body: (
        <>
          {P(
            "The STAR*D study, the largest real-world antidepressant sequencing trial, showed that remission rates decline with each additional medication step: approximately 37% after the first, 31% after the second, 14% after the third, and 13% after the fourth. After two or three adequate trials, a psychiatrist may recommend a different approach.",
          )}
        </>
      ),
    },
    {
      heading: "Non-Medication Options After Two Trials",
      body: (
        <>
          {P(
            <>
              <a
                href="/services/tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                TMS
              </a>{" "}
              is FDA-cleared for adults whose depression has not improved on
              antidepressants.{" "}
              <a
                href="/saint"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT®
              </a>{" "}
              is an accelerated, fMRI-guided form of TMS delivered in five days.{" "}
              <a
                href="/services/spravato"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SPRAVATO®
              </a>{" "}
              (esketamine) is an FDA-approved nasal spray for
              treatment-resistant depression. Each has specific eligibility
              criteria, risks, and evidence limits.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "What to Discuss With Your Psychiatrist",
      body: (
        <>
          {P(
            "Ask: Were my previous trials truly adequate? Should I try a different medication class or augmentation? Am I a candidate for TMS, SAINT®, or SPRAVATO®? What does my insurance cover? What is the plan if the next step doesn't work?",
          )}
          {P(
            <>
              <a
                href="/depression-treatment-not-working"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                See our full guide on when treatment isn't working
              </a>
              .
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation: "Rush AJ, et al. STAR*D report. Am J Psychiatry. 2006.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/what-happens-when-two-antidepressants-dont-work#webpage`,
        name: "What Happens When Two Antidepressants Don't Work?",
        url: `${SITE_URL}/learn/what-happens-when-two-antidepressants-dont-work`,
        description:
          "When two antidepressants don't work, TMS, SAINT®, and SPRAVATO® may be options.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/what-happens-when-two-antidepressants-dont-work#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("When Two Antidepressants Don't Work")),
    ],
  },
  relatedLinks: [
    {
      title: "When Depression Treatment Isn't Working",
      href: "/depression-treatment-not-working",
      description: "Full hub with all options",
    },
    {
      title: "What Is Treatment-Resistant Depression?",
      href: "/learn/what-is-treatment-resistant-depression",
      description: "Definition and options",
    },
    {
      title: "How Many Antidepressants Before TMS?",
      href: "/learn/how-many-antidepressants-before-tms",
      description: "Insurance criteria",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate",
      description: "Request a consultation",
    },
  ],
};
